import React, { useCallback, useContext, useEffect, useState } from "react";
import { NativeAPI } from "src/utils/deviceUtils";
import TranslationsContext from "src/contexts/TranslationsContext";
import styles from "./FirstReads.module.scss";
import { newMetricsWithContext } from "src/utils/metricsUtils";
import { getProgramLogo } from "src/utils/programLogoUtils";
import DeviceContext from "src/contexts/DeviceContext";
import debug from "src/utils/debugUtils";

type PropTypes = {
    item: QuickViewAsinMetadata;
    subscribeToEmailCallback?: (subscribe: boolean) => void;
};

const checkForEmailUpsell = (buyingOption?: AapiBuyingOption): boolean => {
    return !!buyingOption?.callToAction?.entity?.oneClick?.data?.parameters?.find(param => param.name === "items[0].optional.botmSignupForEmail")
        || debug.get("forceAfrEmailUpsell");
};

const checkIfPrime = (buyingOption?: AapiBuyingOption): boolean => {
    return buyingOption?.callToAction?.entity?.oneClick?.data?.parameters?.find(param => param.name === "items[0].action.botmType")?.value === "PRIME";
};

export const FirstReadsUpsell: React.FC<PropTypes> = ({ item }: PropTypes) => {
    const translations = useContext(TranslationsContext);
    const deviceContext = useContext(DeviceContext);
    const buyingOption = item.primaryPurchaseBuyingOption;
    const afrUpsellText = buyingOption?.kindleProgram?.entity?.shortMessage;
    const afrLearnMoreText = buyingOption?.kindleProgram?.entity?.learnMore?.data?.displayString ?? translations.getText("learn-more");
    const showBonusBadge = debug.get("forceBonusShortReadBadge");
    const metrics = newMetricsWithContext("FirstReads", item.asin);

    const openAfrLearnMore = () => {
        metrics.recordBehavioralMetric(`AfrUpsellLearnMore.click`, 1);
        NativeAPI.openWebPage(buyingOption?.kindleProgram?.entity?.learnMore?.url ?? "/firstreads");
    };

    const afrLogo = getProgramLogo("FIRST_READS", deviceContext.theme, deviceContext.domain, deviceContext.enableKuRebrand);

    return (<div className={`${styles.afrUpsell} ${styles[deviceContext.theme]}`}>
        <div className={styles.afrLogoContainer}>
            <img className={styles.afrLogo} src={afrLogo?.src} alt={translations.getText(afrLogo?.altKey ?? "")} />
        </div>
        <div className={styles.afrUpsellTextContainer}>
            <div className={styles.afrUpsellText}>
                {/* TODO: Wire up actual data once we get the badge data from the back end. */}
                {showBonusBadge && <span className={styles.addonBadge}>Bonus short read</span>}
                {afrUpsellText + " "}
                <span className={`${styles.learnMore} ${styles.activeText}`} role="button" tabIndex={0} onClick={openAfrLearnMore}>{afrLearnMoreText}</span>
            </div>
        </div>
    </div>);
};

export const FirstReadsEmailUpsell: React.FC<PropTypes> = ({ item, subscribeToEmailCallback }: PropTypes) => {
    const translations = useContext(TranslationsContext);
    const buyingOption = item.primaryPurchaseBuyingOption;
    const hasEmailUpsell = checkForEmailUpsell(buyingOption) || debug.get("forceAfrEmailUpsell");
    const [subscribeToEmail, setSubscribeToEmail] = useState(true);
    const isPrime = checkIfPrime(buyingOption);

    const tapHandler = useCallback(() => { setSubscribeToEmail(!subscribeToEmail); }, [subscribeToEmail]);

    useEffect(() => {
        if (hasEmailUpsell) {
            subscribeToEmailCallback?.(subscribeToEmail);
        }
    }, [hasEmailUpsell, subscribeToEmail, subscribeToEmailCallback]);

    if (hasEmailUpsell) {
        return (<div className={styles.afrEmailUpsell}>
            <input
                className={styles.checkbox}
                type="checkbox"
                checked={subscribeToEmail}
                onChange={tapHandler}
            />
            <label className={styles.checkboxLabel}>
                {isPrime
                    ? translations.getText("pf-afr-prime-email-subscribe-message")
                    : translations.getText("pf-afr-nonprime-email-subscribe-message")
                }
            </label>
        </div>);
    }
    return null;
};

export const FirstReadsLegalText: React.FC<PropTypes> = ({ item }: PropTypes) => {
    const translations = useContext(TranslationsContext);
    const deviceContext = useContext(DeviceContext);

    return (<div className={`${styles.soldByText} ${styles[deviceContext.theme]}`}>
        {translations.formatText("sold-by-seller-of-record", { sellerOfRecord: item.sellerOfRecord })}
    </div>);
};
